import { FormControl, InputLabel, MenuItem, Select as BaseSelect, SelectChangeEvent } from "@mui/material"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Option } from "../../services/ntuc/userService/staticData"
import SelectValidator from "../../services/validation/personal-and-contact-details-form/SelectValidator"
import { FormFieldProps } from "../FormFieldProps/FormFieldProps"

interface SelectProps extends FormFieldProps<Option> {
    options: Option[],
    label: string,
}

const placeholderOption: Option = {
    code: `placeholder`,
    value: `select_placeholder`,
}

// Select with placeholder
function Select(props: SelectProps) {
    const { t } = useTranslation(`signup`)
    const { options, label, handleChange, errorText, defaultValue, isDisabled } = props
    const [error, setError] = useState(false)

    const displayLabel: string = t(label)
    const labelId = `${label}-select-label`
    const id = `${label}-select`
    const testId = `test-${label}-select`
    const selectValidator = useMemo(() => new SelectValidator(options), [options])

    const placeholder = () => {
        return <MenuItem disabled value={placeholderOption.code}><em>{t(placeholderOption.value)}</em></MenuItem>
    }

    const choicesMenuItems = options.map((element: Option, index) => {
        return (
            <MenuItem data-testid={element.code} value={element.code} key={index}>{element.value}</MenuItem>
        )
    })

    useEffect(() => {
        setError(errorText !== undefined)
    }, [errorText])

    const handleOnChange = (event: SelectChangeEvent, child: any) => {
        const option = { code: event.target.value, value: child.props.children }
        validateField(option)
        handleChange(option)
    }

    const validateField = (option: Option) => {
        setError(selectValidator.validate(option) !== undefined)
    }

    return (
        <FormControl error={error} fullWidth margin="normal">
            <InputLabel shrink id={labelId}>
                {displayLabel}
            </InputLabel>
            <BaseSelect
                displayEmpty
                labelId={labelId}
                id={id}
                data-testid={testId}
                label={displayLabel}
                defaultValue={`placeholder`}
                value={(defaultValue && defaultValue.code) ? defaultValue.code : placeholderOption.code}
                onChange={handleOnChange}
                disabled={isDisabled}
            >
                {placeholder()}
                {choicesMenuItems}
            </BaseSelect>
        </FormControl>
    )
}

export default Select
